import { useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RedeemIcon from '@mui/icons-material/Redeem';
import type { RootState } from '../../store';
import { useAdminMeQuery, useAdminSolicitarAcessoMutation } from '../../api';

/**
 * Gate de acesso ao backoffice do cardápio. Só libera o conteúdo se a empresa
 * tem o módulo "Cardápio Sischef" (`me.temAcessoCardapio`). Caso contrário,
 * mostra a tela de "solicitar acesso" (espelha a `unauthorized` do sischef-app).
 *
 * Envolve as rotas do backoffice (`AdminLayout` e `/admin/criar-loja`). A
 * entrada `/admin` (AdminPage) não passa por aqui: ela só captura o token e
 * redireciona para uma rota guardada, então o gate aparece após o redirect.
 */
export function GuardAcessoCardapio({ children }: { children: ReactNode }): JSX.Element {
  const token = useSelector((s: RootState) => s.adminAuth.token);
  const { data: me, isLoading } = useAdminMeQuery(undefined, { skip: !token });

  // Sem token, volta pra entrada (que orienta a abrir pelo Sischef).
  if (!token) return <Navigate to="/admin" replace />;

  if (isLoading || !me) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!me.temAcessoCardapio) {
    return <SolicitarAcessoCardapio nomeUnidade={me.nomeUnidade} />;
  }

  return <>{children}</>;
}

interface SolicitarAcessoCardapioProps {
  nomeUnidade: string | null;
}

function SolicitarAcessoCardapio({ nomeUnidade }: SolicitarAcessoCardapioProps): JSX.Element {
  const [solicitar, status] = useAdminSolicitarAcessoMutation();
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function aoSolicitar(): Promise<void> {
    setErro(null);
    try {
      await solicitar().unwrap();
      setEnviado(true);
    } catch (err) {
      const msg = (err as { data?: { mensagem?: string } })?.data?.mensagem;
      setErro(msg ?? 'Não foi possível enviar a solicitação. Tente novamente.');
    }
  }

  return (
    <Container maxWidth="sm" sx={{ pt: { xs: 4, md: 8 } }}>
      <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 } }}>
        <Stack alignItems="center" spacing={2} textAlign="center">
          <RedeemIcon sx={{ fontSize: 64, color: 'primary.main' }} />
          <Typography variant="h5">Funcionalidade indisponível</Typography>
          <Typography color="text.secondary">
            O módulo <strong>Sischef Cardápio</strong> não está habilitado
            {nomeUnidade ? ` para a empresa ${nomeUnidade}` : ' para a sua empresa'}. Clique
            abaixo para solicitar a liberação — nossa equipe entrará em contato.
          </Typography>

          {enviado ? (
            <Alert severity="success" sx={{ width: '100%', textAlign: 'left' }}>
              Solicitação enviada com sucesso. Logo entraremos em contato.
            </Alert>
          ) : (
            <>
              {erro && (
                <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>
                  {erro}
                </Alert>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={aoSolicitar}
                disabled={status.isLoading}
                startIcon={status.isLoading ? <CircularProgress size={18} color="inherit" /> : <RedeemIcon />}
              >
                Solicitar acesso
              </Button>
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
